"use strict";
//
// Copyright © 2020, 2021 Anticrm Platform Contributors.
// Copyright © 2026 Intabia Fusion.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.platformNowDiff = exports.platformNow = exports.loginSocialTypes = exports.TimeRateLimiter = exports.isEnum = exports.RateLimiter = exports.DocManager = exports.AggregateValue = exports.AggregateValueData = void 0;
exports.generateId = generateId;
exports.generateUuid = generateUuid;
exports.isId = isId;
exports.getCurrentAccount = getCurrentAccount;
exports.setCurrentAccount = setCurrentAccount;
exports.escapeLikeForRegexp = escapeLikeForRegexp;
exports.toFindResult = toFindResult;
exports.isWorkspaceCreating = isWorkspaceCreating;
exports.docKey = docKey;
exports.isFullTextAttribute = isFullTextAttribute;
exports.isIndexedAttribute = isIndexedAttribute;
exports.toIdMap = toIdMap;
exports.concatLink = concatLink;
exports.fillDefaults = fillDefaults;
exports.mergeQueries = mergeQueries;
exports.cutObjectArray = cutObjectArray;
exports.includesAny = includesAny;
exports.checkPermission = checkPermission;
exports.checkForbiddenPermission = checkForbiddenPermission;
exports.getRoleAttributeLabel = getRoleAttributeLabel;
exports.getFullTextIndexableAttributes = getFullTextIndexableAttributes;
exports.getFullTextContext = getFullTextContext;
exports.isClassIndexable = isClassIndexable;
exports.reduceCalls = reduceCalls;
exports.isOwnerOrMaintainer = isOwnerOrMaintainer;
exports.hasAccountRole = hasAccountRole;
exports.getBranding = getBranding;
exports.fillConfiguration = fillConfiguration;
exports.pluginFilterTx = pluginFilterTx;
exports.combineAttributes = combineAttributes;
exports.buildSocialIdString = buildSocialIdString;
exports.parseSocialIdString = parseSocialIdString;
exports.pickPrimarySocialId = pickPrimarySocialId;
exports.notEmpty = notEmpty;
exports.unique = unique;
exports.uniqueNotEmpty = uniqueNotEmpty;
exports.hashWorkspace = hashWorkspace;
exports.selectUrl = selectUrl;
const platform_1 = require("@hcengineering/platform");
const fast_equals_1 = require("fast-equals");
const benchmark_1 = require("./benchmark");
const classes_1 = require("./classes");
const component_1 = __importDefault(require("./component"));
const predicate_1 = require("./predicate");
const tx_1 = require("./tx");
function toHex(value, chars) {
    const result = value.toString(16);
    if (result.length < chars) {
        return '0'.repeat(chars - result.length) + result;
    }
    return result;
}
let counter = (Math.random() * (1 << 24)) | 0;
const random = toHex((Math.random() * (1 << 24)) | 0, 6) + toHex((Math.random() * (1 << 16)) | 0, 4);
function timestamp() {
    const time = (Date.now() / 1000) | 0;
    return toHex(time, 8);
}
function count() {
    const val = counter++ & 0xffffff;
    return toHex(val, 6);
}
/**
 * @public
 * @returns
 */
function generateId(join = '') {
    return (timestamp() + join + random + join + count());
}
function generateUuid() {
    // Consider own implementation if it will be slow
    return crypto.randomUUID();
}
/** @public */
function isId(value) {
    return typeof value === 'string' && /^[0-9a-f]{24,24}$/.test(value);
}
let currentAccount;
/**
 * @public
 * @returns
 */
function getCurrentAccount() {
    return currentAccount;
}
/**
 * @public
 * @param account -
 */
function setCurrentAccount(account) {
    currentAccount = account;
}
/**
 * @public
 */
function escapeLikeForRegexp(value) {
    return value.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
}
/**
 * @public
 */
function toFindResult(docs, total, lookupMap) {
    const length = total ?? docs.length;
    if (Object.keys(lookupMap ?? {}).length === 0) {
        lookupMap = undefined;
    }
    return Object.assign(docs, { total: length, lookupMap });
}
/**
 * @public
 */
function isWorkspaceCreating(mode) {
    if (mode === undefined) {
        return false;
    }
    return ['pending-creation', 'creating'].includes(mode);
}
/**
 * @public
 */
function docKey(name, _class) {
    return _class === undefined || _class !== component_1.default.class.Doc ? name : `${_class}%${name}`;
}
/**
 * @public
 */
function isFullTextAttribute(attr) {
    return (attr.index === classes_1.IndexKind.FullText ||
        attr.type._class === component_1.default.class.TypeBlob ||
        attr.type._class === component_1.default.class.EnumOf ||
        attr.type._class === component_1.default.class.TypeCollaborativeDoc);
}
/**
 * @public
 */
function isIndexedAttribute(attr) {
    return attr.index === classes_1.IndexKind.Indexed || attr.index === classes_1.IndexKind.IndexedDsc;
}
/**
 * @public
 */
function toIdMap(arr) {
    return new Map(arr.map((p) => [p._id, p]));
}
/**
 * @public
 */
function concatLink(host, path) {
    if (!host.endsWith('/') && !path.startsWith('/')) {
        return `${host}/${path}`;
    }
    else if (host.endsWith('/') && path.startsWith('/')) {
        const newPath = path.slice(1);
        return `${host}${newPath}`;
    }
    else {
        return `${host}${path}`;
    }
}
/**
 * @public
 */
function fillDefaults(hierarchy, object, _class) {
    const baseClass = hierarchy.isDerived(_class, component_1.default.class.AttachedDoc) ? component_1.default.class.AttachedDoc : component_1.default.class.Doc;
    const attributes = hierarchy.getAllAttributes(_class, baseClass);
    for (const attribute of attributes) {
        if (attribute[1].defaultValue != null) {
            if (object[attribute[0]] === undefined) {
                // Clone default value as it might be an object (e.g. array)
                ;
                object[attribute[0]] = structuredClone(attribute[1].defaultValue);
            }
        }
    }
    return object;
}
/**
 * @public
 */
class AggregateValueData {
    name;
    _id;
    space;
    rank;
    category;
    constructor(name, _id, space, rank, category) {
        this.name = name;
        this._id = _id;
        this.space = space;
        this.rank = rank;
        this.category = category;
    }
    getRank() {
        return this.rank ?? '';
    }
}
exports.AggregateValueData = AggregateValueData;
/**
 * @public
 */
class AggregateValue {
    name;
    values;
    constructor(name, values) {
        this.name = name;
        this.values = values;
    }
}
exports.AggregateValue = AggregateValue;
/**
 * @public
 */
class DocManager {
    docs;
    byId;
    constructor(docs) {
        this.docs = docs;
        this.byId = toIdMap(docs);
    }
    get(ref) {
        return this.byId.get(ref);
    }
    getDocs() {
        return this.docs;
    }
    getIdMap() {
        return this.byId;
    }
    filter(predicate) {
        return this.docs.filter(predicate);
    }
}
exports.DocManager = DocManager;
/**
 * @public
 */
class RateLimiter {
    idCounter = 0;
    processingQueue = new Map();
    last = 0;
    rate;
    queue = [];
    constructor(rate) {
        this.rate = rate;
    }
    notify = [];
    async exec(op, args) {
        const processingId = this.idCounter++;
        while (this.processingQueue.size >= this.rate) {
            await new Promise((resolve) => {
                this.notify.push(resolve);
            });
        }
        try {
            const p = op(args);
            this.processingQueue.set(processingId, p);
            return await p;
        }
        finally {
            this.processingQueue.delete(processingId);
            const n = this.notify.shift();
            if (n !== undefined) {
                n();
            }
        }
    }
    async add(op, args, errHandler) {
        while (this.processingQueue.size >= this.rate) {
            await new Promise((resolve) => {
                this.notify.push(resolve);
            });
        }
        void this.exec(op, args).catch((err) => {
            if (errHandler !== undefined) {
                errHandler(err);
            }
            console.error('Failed to execute in rate limitter', err);
        });
    }
    async waitProcessing() {
        while (this.processingQueue.size > 0) {
            await new Promise((resolve) => {
                this.notify.push(resolve);
            });
        }
    }
}
exports.RateLimiter = RateLimiter;
function mergeQueries(query1, query2) {
    const keys1 = Object.keys(query1);
    const keys2 = Object.keys(query2);
    const query = {};
    for (const key of keys1) {
        if (!keys2.includes(key)) {
            Object.assign(query, { [key]: query1[key] });
        }
    }
    for (const key of keys2) {
        if (!keys1.includes(key)) {
            Object.assign(query, { [key]: query2[key] });
        }
        else {
            const value = mergeField(query1[key], query2[key]);
            if (value !== undefined) {
                Object.assign(query, { [key]: value });
            }
        }
    }
    return query;
}
function mergeField(field1, field2) {
    // this is a special predicate that causes query never return any docs
    // it is used in cases when queries intersection is empty
    const never = { $in: [] };
    // list of ignored predicates, handled separately
    const ignored = ['$in', '$nin', '$ne'];
    const isPredicate1 = (0, predicate_1.isPredicate)(field1);
    const isPredicate2 = (0, predicate_1.isPredicate)(field2);
    if (isPredicate1 && isPredicate2) {
        // $in, $nin, $eq are related fields so handle them separately here
        const result = getInNiN(field1, field2);
        const keys1 = Object.keys(field1);
        const keys2 = Object.keys(field2);
        for (const key of keys1) {
            if (ignored.includes(key))
                continue;
            if (!keys2.includes(key)) {
                Object.assign(result, { [key]: field1[key] });
            }
            else {
                const value = mergePredicateWithPredicate(key, field1[key], field2[key]);
                if (value !== undefined) {
                    Object.assign(result, { [key]: value });
                }
            }
        }
        for (const key of keys2) {
            if (ignored.includes(key))
                continue;
            if (!keys1.includes(key)) {
                Object.assign(result, { [key]: field2[key] });
            }
        }
        return Object.keys(result).length > 0 ? result : undefined;
    }
    else if (isPredicate1 || isPredicate2) {
        // when one field is a predicate and the other is a simple value
        // we need to ensure that the value matches predicate
        const predicate = isPredicate1 ? field1 : field2;
        const value = isPredicate1 ? field2 : field1;
        for (const x in predicate) {
            const result = mergePredicateWithValue(x, predicate[x], value);
            if (Array.isArray(result?.$in) &&
                result.$in.length > 0 &&
                Array.isArray(result?.$nin) &&
                result.$nin.length === 0) {
                delete result.$nin;
            }
            if (result !== undefined) {
                return result;
            }
        }
        // if we reached here, the value does not match the predicate
        return never;
    }
    else {
        // both are not predicates, can filter only when values are equal
        return (0, fast_equals_1.deepEqual)(field1, field2) ? field1 : never;
    }
}
function mergePredicateWithPredicate(predicate, val1, val2) {
    if (val1 === undefined)
        return val2;
    if (val2 === undefined)
        return val1;
    switch (predicate) {
        case '$lt':
            return val1 < val2 ? val1 : val2;
        case '$lte':
            return val1 <= val2 ? val1 : val2;
        case '$gt':
            return val1 > val2 ? val1 : val2;
        case '$gte':
            return val1 >= val2 ? val1 : val2;
    }
    // TODO we should properly support all available predicates here
    // until then, fallback to the first predicate value
    return val1;
}
function mergePredicateWithValue(predicate, val1, val2) {
    switch (predicate) {
        case '$in':
            return Array.isArray(val1) && val1.includes(val2) ? val2 : undefined;
        case '$nin':
            return Array.isArray(val1) && !val1.includes(val2) ? val2 : undefined;
        case '$lt':
            return val2 < val1 ? val2 : undefined;
        case '$lte':
            return val2 <= val1 ? val2 : undefined;
        case '$gt':
            return val2 > val1 ? val2 : undefined;
        case '$gte':
            return val2 >= val1 ? val2 : undefined;
        case '$ne':
            return val1 !== val2 ? val2 : undefined;
    }
    // TODO we should properly support all available predicates here
    // until then, fallback to the non-predicate value
    return val2;
}
function getInNiN(query1, query2) {
    const aIn = typeof query1 === 'object' && '$in' in query1 ? query1.$in : undefined;
    const bIn = typeof query2 === 'object' && '$in' in query2 ? query2.$in : undefined;
    const aNIn = (typeof query1 === 'object' && '$nin' in query1 ? query1.$nin : undefined) ??
        (typeof query1 === 'object' && query1.$ne !== undefined ? [query1.$ne] : []);
    const bNIn = (typeof query2 === 'object' && '$nin' in query2 ? query2.$nin : undefined) ??
        (typeof query1 === 'object' && query2.$ne !== undefined ? [query2.$ne] : []);
    const finalNin = Array.from(new Set([...aNIn, ...bNIn]));
    // we must keep $in if it was in the original query
    if (aIn !== undefined || bIn !== undefined) {
        const finalIn = aIn !== undefined && bIn !== undefined
            ? aIn.length - bIn.length < 0
                ? bIn.filter((c) => aIn.includes(c))
                : aIn.filter((c) => bIn.includes(c))
            : (aIn ?? bIn);
        return { $in: finalIn.filter((p) => !finalNin.includes(p)) };
    }
    // try to preserve original $ne instead of $nin
    if ((typeof query1 === 'object' && '$ne' in query1) || (typeof query2 === 'object' && '$ne' in query2)) {
        if (finalNin.length === 1) {
            return { $ne: finalNin[0] };
        }
    }
    if (finalNin.length > 0) {
        return { $nin: finalNin };
    }
    return {};
}
function cutObjectArray(obj) {
    if (obj == null) {
        return obj;
    }
    const r = {};
    for (const key of Object.keys(obj)) {
        if (Array.isArray(obj[key])) {
            if (obj[key].length > 3) {
                Object.assign(r, { [key]: [...obj[key].slice(0, 3), `... and ${obj[key].length - 3} more`] });
            }
            else
                Object.assign(r, { [key]: obj[key] });
            continue;
        }
        if (typeof obj[key] === 'object' && obj[key] !== null) {
            Object.assign(r, { [key]: cutObjectArray(obj[key]) });
            continue;
        }
        Object.assign(r, { [key]: obj[key] });
    }
    return r;
}
function includesAny(arr1, arr2) {
    if (arr1 == null || arr1.length === 0 || arr2 == null || arr2.length === 0) {
        return false;
    }
    return arr1.some((m) => arr2.includes(m));
}
const isEnum = (e) => (token) => {
    return typeof token === 'string' && Object.values(e).includes(token);
};
exports.isEnum = isEnum;
async function checkPermission(client, _id, _space, space) {
    const arePermissionsDisabled = (0, platform_1.getMetadata)(component_1.default.metadata.DisablePermissions) ?? false;
    if (arePermissionsDisabled)
        return true;
    return await hasPermission(client, _id, _space, space);
}
async function checkForbiddenPermission(client, _id, _space, space) {
    const arePermissionsDisabled = (0, platform_1.getMetadata)(component_1.default.metadata.DisablePermissions) ?? false;
    if (arePermissionsDisabled)
        return false;
    return await hasPermission(client, _id, _space, space);
}
async function hasPermission(client, _id, _space, space) {
    space = space ?? (await client.findOne(component_1.default.class.TypedSpace, { _id: _space }));
    const type = await client
        .getModel()
        .findOne(component_1.default.class.SpaceType, { _id: space?.type }, { lookup: { _id: { roles: component_1.default.class.Role } } });
    const mixin = type?.targetClass;
    if (space === undefined || type === undefined || mixin === undefined) {
        return false;
    }
    const me = getCurrentAccount();
    const asMixin = client.getHierarchy().as(space, mixin);
    const myRoles = type.$lookup?.roles?.filter((role) => (asMixin[role._id] ?? []).includes(me.uuid) === true);
    if (myRoles === undefined) {
        return false;
    }
    const myPermissions = new Set(myRoles.flatMap((role) => role.permissions));
    return myPermissions.has(_id);
}
/**
 * @public
 */
function getRoleAttributeLabel(roleName) {
    return (0, platform_1.getEmbeddedLabel)(`Role: ${roleName.trim()}`);
}
/**
 * @public
 */
function getFullTextIndexableAttributes(hierarchy, clazz, skipDocs = false) {
    const allAttributes = hierarchy.getAllAttributes(clazz);
    const result = [];
    for (const [, attr] of allAttributes) {
        if (skipDocs && (attr.attributeOf === component_1.default.class.Doc || attr.attributeOf === component_1.default.class.AttachedDoc)) {
            continue;
        }
        if (isFullTextAttribute(attr) || isIndexedAttribute(attr)) {
            result.push(attr);
        }
    }
    hierarchy
        .getDescendants(clazz)
        .filter((m) => hierarchy.getClass(m).kind === classes_1.ClassifierKind.MIXIN)
        .forEach((m) => {
        for (const [, v] of hierarchy.getAllAttributes(m, clazz)) {
            if (skipDocs && (v.attributeOf === component_1.default.class.Doc || v.attributeOf === component_1.default.class.AttachedDoc)) {
                continue;
            }
            if (isFullTextAttribute(v) || isIndexedAttribute(v)) {
                result.push(v);
            }
        }
    });
    return result;
}
const ctxKey = 'indexer_ftc';
/**
 * @public
 */
function getFullTextContext(hierarchy, objectClass, contexts) {
    let ctx = hierarchy.getClassifierProp(objectClass, ctxKey);
    if (ctx !== undefined) {
        return ctx;
    }
    if (typeof ctx !== 'string') {
        const anc = hierarchy.getAncestors(objectClass);
        for (const oc of anc) {
            const ctx = contexts.get(oc);
            if (ctx !== undefined) {
                hierarchy.setClassifierProp(objectClass, ctxKey, ctx);
                return ctx;
            }
        }
    }
    ctx = {
        toClass: objectClass,
        fullTextSummary: false,
        forceIndex: false
    };
    hierarchy.setClassifierProp(objectClass, ctxKey, ctx);
    return ctx;
}
/**
 * @public
 */
function isClassIndexable(hierarchy, c, contexts) {
    const indexed = hierarchy.getClassifierProp(c, 'class_indexed');
    if (indexed !== undefined) {
        return indexed;
    }
    const domain = hierarchy.findDomain(c);
    if (domain === undefined) {
        hierarchy.setClassifierProp(c, 'class_indexed', false);
        return false;
    }
    if (domain === tx_1.DOMAIN_TX ||
        domain === classes_1.DOMAIN_MODEL ||
        domain === classes_1.DOMAIN_BLOB ||
        domain === 'preference' ||
        domain === classes_1.DOMAIN_TRANSIENT ||
        domain === 'settings' ||
        domain === benchmark_1.DOMAIN_BENCHMARK) {
        hierarchy.setClassifierProp(c, 'class_indexed', false);
        return false;
    }
    const indexMixin = hierarchy.classHierarchyMixin(c, component_1.default.mixin.IndexConfiguration);
    if (indexMixin?.searchDisabled !== undefined && indexMixin?.searchDisabled) {
        hierarchy.setClassifierProp(c, 'class_indexed', false);
        return false;
    }
    const attrs = getFullTextIndexableAttributes(hierarchy, c, true);
    for (const d of hierarchy.getDescendants(c)) {
        if (hierarchy.isMixin(d)) {
            attrs.push(...getFullTextIndexableAttributes(hierarchy, d, true));
        }
    }
    let result = true;
    if (attrs.length === 0 && !(getFullTextContext(hierarchy, c, contexts)?.forceIndex ?? false)) {
        result = false;
        // We need check if document has collections with indexable fields.
        const attrs = hierarchy.getAllAttributes(c).values();
        for (const attr of attrs) {
            if (attr.type._class === component_1.default.class.Collection) {
                if (isClassIndexable(hierarchy, attr.type.of, contexts)) {
                    result = true;
                    break;
                }
            }
        }
    }
    hierarchy.setClassifierProp(c, 'class_indexed', result);
    return result;
}
/**
 * Utility method to skip middle update calls, optimistically if update function is called multiple times with few different parameters, only the last variant will be executed.
 * The last invocation is executed after a few cycles, allowing to skip middle ones.
 *
 * This method can be used inside Svelte components to collapse complex update logic and handle interactions.
 */
function reduceCalls(operation) {
    let nextCall;
    let currentCall;
    const next = () => {
        currentCall = nextCall;
        nextCall = undefined;
        if (currentCall !== undefined) {
            void currentCall.op().catch();
        }
    };
    return async function (...args) {
        const myOp = async () => {
            try {
                await operation(...args);
            }
            catch (err) {
                console.error('Error occurred in reduceCalls:', err);
            }
            next();
        };
        nextCall = { op: myOp };
        await Promise.resolve();
        if (currentCall === undefined) {
            next();
        }
    };
}
function isOwnerOrMaintainer() {
    const account = getCurrentAccount();
    if (account == null) {
        return false;
    }
    return hasAccountRole(account, classes_1.AccountRole.Maintainer);
}
function hasAccountRole(acc, targerRole) {
    if (acc == null) {
        return false;
    }
    return classes_1.roleOrder[acc.role] >= classes_1.roleOrder[targerRole];
}
function getBranding(brandings, key) {
    if (key === undefined)
        return null;
    return Object.values(brandings).find((branding) => branding.key === key) ?? null;
}
function fillConfiguration(systemTx, configs) {
    for (const t of systemTx) {
        if (t._class === component_1.default.class.TxCreateDoc) {
            const ct = t;
            if (ct.objectClass === component_1.default.class.PluginConfiguration) {
                configs.set(ct.objectId, tx_1.TxProcessor.createDoc2Doc(ct));
            }
        }
        else if (t._class === component_1.default.class.TxUpdateDoc) {
            const ut = t;
            if (ut.objectClass === component_1.default.class.PluginConfiguration) {
                const c = configs.get(ut.objectId);
                if (c !== undefined) {
                    if (c.system !== true || ut.modifiedBy === component_1.default.account.ConfigUser) {
                        tx_1.TxProcessor.updateDoc2Doc(c, ut);
                    }
                }
            }
        }
    }
}
function pluginFilterTx(excludedPlugins, configs, systemTx) {
    const stx = toIdMap(systemTx);
    const totalExcluded = new Set();
    let msg = '';
    for (const a of excludedPlugins) {
        for (const c of configs.values()) {
            if (a.pluginId === c.pluginId) {
                for (const id of c.transactions) {
                    if (c.mixinFilter != null) {
                        const tx = stx.get(id);
                        const mixinFilter = new Set(c.mixinFilter ?? []);
                        if (tx?._class === component_1.default.class.TxMixin) {
                            const cud = tx;
                            if (mixinFilter.has(cud.mixin)) {
                                totalExcluded.add(id);
                            }
                        }
                    }
                    if (c.objectIdFilter != null) {
                        const objectIdFilter = new Set(c.objectIdFilter ?? []);
                        const tx = stx.get(id);
                        if (tx?._class === component_1.default.class.TxCreateDoc ||
                            tx?._class === component_1.default.class.TxUpdateDoc ||
                            tx?._class === component_1.default.class.TxRemoveDoc) {
                            const cud = tx;
                            if (objectIdFilter.has(cud.objectId)) {
                                totalExcluded.add(id);
                            }
                        }
                        else if (tx?._class === component_1.default.class.TxMixin) {
                            const cud = tx;
                            if (objectIdFilter.has(cud.objectId) || objectIdFilter.has(cud.mixin)) {
                                totalExcluded.add(id);
                            }
                        }
                    }
                    if (c.classFilter !== undefined) {
                        const filter = new Set(c.classFilter);
                        const tx = stx.get(id);
                        if (tx?._class === component_1.default.class.TxCreateDoc ||
                            tx?._class === component_1.default.class.TxUpdateDoc ||
                            tx?._class === component_1.default.class.TxRemoveDoc) {
                            const cud = tx;
                            if (filter.has(cud.objectClass)) {
                                totalExcluded.add(id);
                            }
                        }
                    }
                    else {
                        totalExcluded.add(id);
                    }
                }
                msg += ` ${c.pluginId}:${c.transactions.length}`;
            }
        }
    }
    if (typeof window !== 'undefined') {
        console.log('exclude plugin', msg);
    }
    systemTx = systemTx.filter((t) => !totalExcluded.has(t._id));
    return systemTx;
}
/**
 * @public
 */
class TimeRateLimiter {
    idCounter = 0;
    active = 0;
    last = 0;
    rate;
    period;
    executions = [];
    queue = [];
    notify = [];
    constructor(rate, period = 1000) {
        this.rate = rate;
        this.period = period;
    }
    cleanupExecutions() {
        const now = Date.now();
        this.executions = this.executions.filter((time) => time.running || now - time.time < this.period);
    }
    async exec(op, args) {
        while (this.active >= this.rate || this.executions.length >= this.rate) {
            this.cleanupExecutions();
            if (this.executions.length < this.rate) {
                break;
            }
            await new Promise((resolve) => {
                setTimeout(resolve, this.period / this.rate);
            });
        }
        const v = { time: Date.now(), running: true };
        this.active++;
        try {
            this.executions.push(v);
            const p = op(args);
            return await p;
        }
        finally {
            v.running = false;
            this.active--;
            this.cleanupExecutions();
            const n = this.notify.shift();
            if (n !== undefined) {
                n();
            }
        }
    }
    async waitProcessing() {
        while (this.active > 0) {
            await new Promise((resolve) => {
                this.notify.push(resolve);
            });
        }
    }
}
exports.TimeRateLimiter = TimeRateLimiter;
function combineAttributes(attributes, key, operator, arrayKey) {
    return Array.from(new Set(attributes.flatMap((attr) => {
        if (arrayKey === undefined) {
            return attr[operator]?.[key];
        }
        return Array.isArray(attr[operator]?.[key]?.[arrayKey])
            ? attr[operator]?.[key]?.[arrayKey]
            : attr[operator]?.[key];
    }))).filter((v) => v != null);
}
function buildSocialIdString(key) {
    return `${key.type}:${key.value}`;
}
function parseSocialIdString(id) {
    const [type, value] = id.split(':');
    if (type === undefined || value === undefined) {
        throw new Error(`Social id is not valid: ${id}`);
    }
    if (!Object.values(classes_1.SocialIdType).includes(type)) {
        throw new Error(`Social id type is not valid: ${id}`);
    }
    return { type: type, value };
}
function pickPrimarySocialId(socialIds) {
    const activeSocialIds = socialIds.filter((si) => si.isDeleted !== true);
    if (activeSocialIds.length === 0) {
        throw new Error('No active social ids provided');
    }
    const hulySocialIds = activeSocialIds.filter((si) => si.type === classes_1.SocialIdType.HULY);
    return hulySocialIds[0] ?? activeSocialIds[0];
}
exports.loginSocialTypes = [classes_1.SocialIdType.EMAIL, classes_1.SocialIdType.GOOGLE, classes_1.SocialIdType.GITHUB, classes_1.SocialIdType.OIDC];
function notEmpty(id) {
    return id !== undefined && id !== null && id !== '';
}
function unique(arr) {
    return Array.from(new Set(arr));
}
function uniqueNotEmpty(arr) {
    return unique(arr).filter(notEmpty);
}
var measurements_1 = require("@hcengineering/measurements");
Object.defineProperty(exports, "platformNow", { enumerable: true, get: function () { return measurements_1.platformNow; } });
Object.defineProperty(exports, "platformNowDiff", { enumerable: true, get: function () { return measurements_1.platformNowDiff; } });
/**
 * Hash a workspace name to a number for consistent endpoint selection.
 * Uses Java's String.hashCode() algorithm.
 * @public
 */
function hashWorkspace(dbWorkspaceName) {
    return [...dbWorkspaceName].reduce((hash, c) => (Math.imul(31, hash) + c.charCodeAt(0)) | 0, 0);
}
/**
 * Select a URL from a comma-separated list using hash-based routing.
 * @public
 */
function selectUrl(urls, workspaceId) {
    const urlList = urls
        .split(',')
        .map((it) => it.trim())
        .filter((it) => it.length > 0);
    if (urlList.length === 0) {
        return urls;
    }
    if (urlList.length === 1) {
        return urlList[0];
    }
    const hash = hashWorkspace(workspaceId);
    return urlList[Math.abs(hash % urlList.length)];
}
//# sourceMappingURL=utils.js.map